import { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useObjectTableColumns } from 'components/ObjectTable/ObjectTable';
import { useDefaultTableProps } from 'hooks/useDefaultTableProps';
import { useSortColumnsModal } from 'hooks/useSortColumnsModal';
import { useTableTitle } from 'hooks/useTableTitle';
import { getText } from 'i18n/intl';
import { isEmpty, mapValues } from 'lodash';
import { type TFindObjectsPayload, findMoreObjects } from 'slices/object.slice';
import { useAppSelector } from 'slices/store';
import { canManageObjects } from 'utils/authentication';
import { parseCustomerSignature } from 'utils/customerSignature';
import type { TTEObject } from '@timeedit/types';
import {
  EditableTable,
  type EditableTableProps,
  type GetMore,
  type GetMoreOptions,
  useInfiniteScroll,
} from '@timeedit/ui-components';
import { Button, Typography } from '@timeedit/ui-components/antd';
import {
  ObjectPreviewFilter,
  type TObjectPreviewFilterState,
} from './ObjectPreviewFilter';
import { useFooter } from './hooks/useFooter';
import { useTotal } from './hooks/useTotal';

type TObjectPreviewTableProps = {
  queries: Record<string, unknown>;
  showLinkToObject: boolean;
  virtualOptions?: {
    limitPerPage?: number;
    itemHeight?: number;
    listHeight?: number;
    enable?: boolean;
  };
  footer?: React.ReactNode[];
  rowSelection?: EditableTableProps['rowSelection'];
  showTotalNumber?: boolean;
  onObjectsChange?: (objects: TTEObject[]) => void;
  filter?: TObjectPreviewFilterState;
  onFilterChange?: (value: TObjectPreviewFilterState) => void;
};

const placeholderResponse = {
  data: [],
  page: 0,
  count: 0,
  total: 0,
  hasMore: false,
};

export const ObjectsPreviewTable: FC<TObjectPreviewTableProps> = (props) => {
  const {
    queries = {},
    virtualOptions = {},
    footer,
    rowSelection,
    showTotalNumber,
    onObjectsChange,
    filter: controlledFilter,
    onFilterChange,
  } = props;
  const {
    limitPerPage = 50,
    itemHeight = 36,
    listHeight = 300,
    enable: virtualEnable = true,
  } = virtualOptions;

  const isFilterControlled = controlledFilter != null;
  const [internalFilter, setInternalFilter] =
    useState<TObjectPreviewFilterState>({ typeId: null });

  const activeFilter: TObjectPreviewFilterState = isFilterControlled
    ? controlledFilter
    : internalFilter;

  const objectTypeId = activeFilter.typeId ?? null;
  const hasQuery = !isEmpty(queries);
  const authUser = useAppSelector((state) => state.auth.user);

  const hasAccessToObjectPath = useMemo(
    () => canManageObjects(authUser),
    [authUser]
  );

  const getMoreObjects = useCallback<GetMore<TTEObject>>(
    async (options: GetMoreOptions) => {
      if (!hasQuery) {
        return placeholderResponse;
      }

      try {
        const { results, page, totalPages, totalResults } =
          await findMoreObjects({
            ...queries,
            typeId: objectTypeId != null ? objectTypeId : undefined,
            active: 'TRUE',
            // `useObjectTableColumns` renders reference columns for this table
            includeReferenceFields: true,
            ...options,
          } satisfies TFindObjectsPayload);

        return {
          data: results,
          page,
          count: results.length,
          total: totalResults,
          hasMore: page < totalPages,
        };
      } catch (error) {
        console.error('Error fetching objects:', error);
        return placeholderResponse;
      }
    },
    [queries, objectTypeId, hasQuery]
  );

  const user = useAppSelector((state) => state.auth.user);

  const objectPath = useMemo(() => {
    const signature = parseCustomerSignature(user?.customerSignature);
    if (!signature) return null;

    return `/${signature}/objects`;
  }, [user?.customerSignature]);

  const columns = useObjectTableColumns(objectTypeId);

  const updatedColumns = useMemo(() => {
    const unsortableObjects = mapValues(columns, (col) => ({
      ...col,
      sorter: undefined,
      ellipsis: true,
    }));

    unsortableObjects.active.ellipsis = false;

    if (objectPath) {
      unsortableObjects.extId.render = (extId, obj) => {
        if (hasAccessToObjectPath) {
          return (
            <Typography.Link
              underline
              href={`${objectPath}?item1=${obj.id}`}
              target="_blank"
            >
              {extId}
            </Typography.Link>
          );
        }

        return <Typography.Text>{extId}</Typography.Text>;
      };
    }
    return unsortableObjects;
  }, [columns, objectPath, hasAccessToObjectPath]);

  const { sortColumnsModal, openSortColumnsModal, sortedColumns } =
    useSortColumnsModal(updatedColumns);

  const {
    data: objects = [],
    onScroll,
    reset,
    total: filteredAmount,
    loading,
  } = useInfiniteScroll<TTEObject>({
    className: '.rc-virtual-list-holder-inner',
    getMore: getMoreObjects,
    itemHeight: itemHeight,
    containerHeight: listHeight,
    limit: limitPerPage,
    virtual: true,
    initialFetch: hasQuery,
  });

  useEffect(() => {
    reset();
  }, [queries, objectTypeId, reset]);

  useEffect(() => {
    onObjectsChange?.(objects);
  }, [objects, onObjectsChange]);

  const handleFilterChange = useCallback(
    (value: TObjectPreviewFilterState) => {
      if (!isFilterControlled) {
        setInternalFilter(value);
      }
      onFilterChange?.(value);
    },
    [isFilterControlled, onFilterChange]
  );

  const total = useTotal(queries, !!showTotalNumber);

  const titleActions = useMemo(
    () => ({
      right: (
        <Button onClick={openSortColumnsModal} size="small">
          {getText('ui.table.edit_columns')}
        </Button>
      ),
    }),
    [openSortColumnsModal]
  );

  const title = useTableTitle({
    total,
    filteredAmount: hasQuery ? filteredAmount : 0,
    actions: titleActions,
    resultType: 'objects',
    showTotalNumber,
  });

  const [ref, defaultTableProps] = useDefaultTableProps(0, 'extId');

  const footerContent: EditableTableProps['footer'] = useFooter(footer);

  return (
    <div className="objects-preview-container">
      <ObjectPreviewFilter
        queries={queries}
        value={activeFilter}
        onChange={handleFilterChange}
        showLinkToObject={props.showLinkToObject}
      />
      <div
        ref={ref}
        style={virtualEnable ? { height: `${listHeight}px` } : undefined}
      >
        <EditableTable
          {...defaultTableProps}
          loading={hasQuery && loading}
          title={title}
          dataSource={objects}
          columns={sortedColumns as any}
          virtual={virtualEnable}
          fitColumns={false}
          onScroll={onScroll}
          rowSelection={rowSelection}
          {...((footer?.length ?? 0) > 0 ? { footer: footerContent } : {})}
        />
        {sortColumnsModal}
      </div>
    </div>
  );
};
